/** @jsxImportSource @emotion/react */
import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import { Button } from '@mui/material';
import {
  SearchBox,
  SearchRows,
  SearchCols,
  SearchBoxRow,
  InputBox,
} from 'components/layouts/SearchBox';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  HalfContetntLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ExcpPartner } from 'models/admin/ExcpPartner';
import { getExcpList } from 'apis/admin/ExcpPartner';
import { CommonUtil } from 'utils/CommonUtil';
import CustomSwitch from 'components/selects/CustomSwitch';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { Employee } from 'models/admin/Employee';
import { useLoading } from 'components/process/Loading';

type Props = {
  open: boolean;
  close: () => void;
  title?: string;
  mode?: 'cmn' | 'fa';
  condition?: {
    excpId?: string;
    excpNm?: string;
  };
  selectedIds?: string[]; // 선택된 협력사ID (excpId)
  singleSelect?: boolean;
  isOnlyUsed?: boolean; // 사용여부 Y 고정 (default: false) true인 경우 사용여부 조건 미노출
  onCallback: (item: ExcpPartner | ExcpPartner[] | null) => void;
};
const PartnerModal = ({
  open,
  close,
  title,
  mode = 'cmn',
  onCallback,
  singleSelect = true,
  isOnlyUsed = false,
  selectedIds = [],
  ...props
}: Props) => {
  const { t } = useTranslation();
  const searchRef = useRef<any>();
  const selectRef = useRef<any>();
  const defaultCondition = Object.assign(
    {
      excpId: '',
      excpNm: '',
      atmtYn: mode === 'fa' ? 'Y' : 'N', // fa여부
      useYn: 'Y',
      rpsnNm: '',
      excpAbbrNmYn: mode === 'fa' ? 'Y' : 'N', // 협력사 약어 필수여부
    },
    props?.condition || {}
  );
  const [condition, setCondition] = useState<ExcpPartner>(defaultCondition);

  useEffect(() => {
    if (!_.isEmpty(condition?.excpId) || !_.isEmpty(condition?.excpNm)) {
      handleSearch(condition);
    }
  }, []);

  useEffect(() => {
    if (mode === 'fa') {
      setCondition({ ...condition, atmtYn: 'Y' });
    }
  }, [mode]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchRef.current?.handleSearch({ ...condition, useYn: isOnlyUsed ? 'Y' : condition.useYn });
    }
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setCondition({ ...condition, [e.target.name]: e.target.value });
  };

  /**
   * 조회 이벤트 처리
   * @param condition
   */
  const handleSearch = async (condition: ExcpPartner) => {
    searchRef.current?.handleSearch({ ...condition, useYn: isOnlyUsed ? 'Y' : condition.useYn });
  };

  /**
   * 선택 이벤트 처리
   */
  const handleConfirm = () => {
    if (singleSelect) {
      const items = searchRef.current?.getRowData();
      onCallback && onCallback((items || []).length ? items[0] : null);
    } else {
      const items = selectRef.current?.getRowData();
      onCallback && onCallback(items);
    }
    close();
  };

  /**
   * Quick Select 이벤트 처리
   * @param item
   */
  const handleQuickSelect = (item: ExcpPartner) => {
    onCallback && onCallback(item);
    close();
  };

  const dialogButtons = [
    <Button
      key="confirm"
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={title || String(t('com.label.협력업체 조회', '협력업체 조회'))}
      open={open}
      size={singleSelect ? 'md' : 'lg'}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className={singleSelect || !isOnlyUsed ? 'twoCol' : ''}>
              <SearchCols>
                <label>{t('com.label.협력업체 ID', '협력업체 ID')}</label>
                <CustomInputWithSearch
                  name="excpId"
                  placeholder={String(
                    t('com.label.협력업체 ID를 입력해 주세요.', '협력업체 ID를 입력해 주세요.')
                  )}
                  value={condition?.excpId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.협력업체명', '협력업체명')}</label>
                <CustomInputWithSearch
                  name="excpNm"
                  placeholder={String(
                    t('com.label.협력업체명을 입력해 주세요.', '협력업체명을 입력해 주세요.')
                  )}
                  value={condition?.excpNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              {!(singleSelect || !isOnlyUsed) && (
                <SearchCols>
                  <label>{t('com.label.대표자명', '대표자명')}</label>
                  <CustomInputWithSearch
                    name="rpsnNm"
                    placeholder={String(
                      t('com.label.대표자명을 입력해주세요.', '대표자명을 입력해주세요.')
                    )}
                    value={condition?.rpsnNm}
                    onChange={handleConditionChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
              )}
            </SearchRows>
            {(singleSelect || !isOnlyUsed) && (
              <SearchRows className="twoCol">
                <SearchCols>
                  <label>{t('com.label.대표자명', '대표자명')}</label>
                  <CustomInputWithSearch
                    name="rpsnNm"
                    placeholder={String(
                      t('com.label.대표자명을 입력해주세요.', '대표자명을 입력해주세요.')
                    )}
                    value={condition?.rpsnNm}
                    onChange={handleConditionChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
                {!isOnlyUsed && (
                  <>
                    <SearchCols>
                      <label htmlFor="useYn">{t('com.label.사용여부', '사용여부')}</label>
                      <CustomSwitch
                        id="useYn"
                        onValue={'Y'}
                        onLabel={String(t('com.label.사용', '사용'))}
                        offValue={'N'}
                        offLabel={String(t('com.label.미사용', '미사용'))}
                        defaultValue={condition?.useYn}
                        onChange={(value) => {
                          setCondition((prev) => ({
                            ...prev,
                            useYn: value,
                          }));
                        }}
                      />
                    </SearchCols>
                  </>
                )}
              </SearchRows>
            )}
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>
      {singleSelect ? (
        <SearchGrid
          ref={searchRef}
          singleSelect={singleSelect}
          defaultExcpId={selectedIds}
          isOnlyUsed={isOnlyUsed}
          defaultCondition={condition}
          onQuickSelect={handleQuickSelect}
        />
      ) : (
        <HalfContetntLayout style={{ marginTop: '24px' }}>
          <div>
            <SearchGrid
              ref={searchRef}
              singleSelect={singleSelect}
              isOnlyUsed={isOnlyUsed}
              defaultCondition={condition}
              addRow={(items) => selectRef.current?.addRowData(items)}
            />
          </div>
          <div>
            <SelectedGrid ref={selectRef} singleSelect={singleSelect} defaultExcpId={selectedIds} />
          </div>
        </HalfContetntLayout>
      )}
    </CustomDialog>
  );
};

interface ContentProps {
  singleSelect: boolean;
  sortable?: boolean;
  isOnlyUsed?: boolean;
  defaultCondition?: ExcpPartner;
  defaultExcpId?: string[];
  onQuickSelect?: (item: ExcpPartner) => void;
  addRow?: (items: ExcpPartner[]) => void;
}

const SearchGrid = forwardRef(
  (
    {
      singleSelect,
      addRow,
      onQuickSelect,
      defaultCondition,
      isOnlyUsed,
      defaultExcpId = [],
    }: ContentProps,
    ref
  ) => {
    const gridRef = useRef<any>();
    const { t } = useTranslation();
    const [rowData, setRowData] = useState<ExcpPartner[]>([]);
    const [checkedItems, setCheckedItems] = useState<ExcpPartner[]>([]);
    const { openLoading } = useLoading();
    const [colWidthMap, setColWidthMap] = useState<any>({
      excpId: 110,
      excpNm: '*',
      rpsnNm: 180,
      useYn: 95,
    });

    useImperativeHandle(ref, () => ({
      handleSearch: (condition) => {
        handleSearch(condition);
      },
      getRowData: () => {
        return checkedItems;
      },
    }));

    useEffect(() => {
      // 협력사 팝업 열릴때 자동 조회
      handleSearch(defaultCondition);
    }, []);

    useEffect(() => {
      if (singleSelect) {
        if ((gridRef.current?.rows || []).length) {
          if ((defaultExcpId || []).length > 0) {
            gridRef.current?.rows.map((o, idx) => {
              o.isSelected = defaultExcpId.includes(o.dataItem.excpId);
            });
          }
        }
      }
    }, [singleSelect, rowData, defaultExcpId]);

    const handleSearch = (condition) => {
      getExcpList(condition).then((result) => {
        setRowData(result || []);
      });
    };

    const onInitialized = (grid) => {
      gridRef.current = grid;

      //columnGroups속성 사용할 경우 -> Column 너비 수동 지정
      grid.resizingColumn.addHandler((s, e) => {
        const cell = s.columnHeaders.getCellElement(0, e.col);
        const col = e.panel.columns[e.col];
        colWidthMap[col.binding] = col.width;
        setColWidthMap(colWidthMap);
      });

      grid.hostElement.addEventListener('click', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const binding = grid.columns[ht.col].binding;
        const item = grid.rows[ht.row].dataItem;
        if (ht.panel === grid.cells) {
          // Quick Select
          if ('btnSelect' === binding) {
            if (singleSelect) {
              onQuickSelect && onQuickSelect(item);
            }
          }
        }
      });

      grid.hostElement.addEventListener('dblclick', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const item = grid.rows[ht.row].dataItem;
        if (item) {
          if (singleSelect) {
            onQuickSelect && onQuickSelect(item);
          }
        }
      });
    };

    const handleAddRow = () => {
      addRow && addRow(checkedItems);
    };

    const layoutDefinition = [
      {
        binding: 'btnSelect',
        header: ' ',
        minWidth: 39,
        width: 39,
        cssClass: 'WijmoRelay',
        cellTemplate: '<Button />',
        visible: singleSelect,
      },
      {
        binding: 'excpId',
        header: String(t('com.label.협력업체 ID', '협력업체 ID')),
        width: colWidthMap.excpId,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'excpNm',
        header: String(t('com.label.협력업체명', '협력업체명')),
        isReadOnly: true,
        width: colWidthMap.excpNm,
        align: 'left',
      },
      {
        binding: 'rpsnNm',
        header: String(t('com.label.대표자명', '대표자명')),
        isReadOnly: true,
        width: colWidthMap.rpsnNm,
        align: 'center',
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        isReadOnly: true,
        width: colWidthMap.useYn,
        align: 'center',
        visible: !isOnlyUsed,
      },
    ];

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('com.label.협력업체', '협력업체')}</h3>
            <span className="total">
              {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
          {!singleSelect && (
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          )}
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={380}
          isReadOnly={true}
          allowPinning={false}
          autoCheck={true}
          rowSelection={singleSelect ? 'single' : 'multiple'}
          excludeFilter={['btnSelect', 'useYn']}
          initialized={onInitialized}
          loadedRows={(grid, e) => {
            const items = grid.collectionView
              ? grid.collectionView.sourceCollection
              : grid.itemsSource;
            if (!items || !Array.isArray(items)) {
              return;
            }

            items.forEach((item, rowIndex) => {
              grid.columns.forEach((col, colIndex) => {
                const value = grid.getCellData(rowIndex, colIndex, false);
                const content = CommonUtil.safeHtmlContents(value, false);

                grid.setCellData(rowIndex, colIndex, content, true);
              });
            });

            grid.invalidate();
          }}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />
      </>
    );
  }
);

const SelectedGrid = forwardRef(({ defaultExcpId }: ContentProps, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<ExcpPartner[]>([]);
  const [checkedItems, setCheckedItems] = useState<ExcpPartner[]>([]);
  const [colWidthMap, setColWidthMap] = useState<any>({
    excpId: 110,
    excpNm: '*',
    rpsnNm: 180,
  });

  useImperativeHandle(ref, () => ({
    addRowData: (items: Employee[]) => {
      addRowData(items);
    },
    getRowData: () => {
      return rowData;
    },
  }));

  useEffect(() => {
    if ((defaultExcpId || []).length > 0) {
      getExcpList({ excpIds: defaultExcpId }).then((result) => {
        addRowData(result || []);
      });
    }
  }, [defaultExcpId]);

  const addRowData = (items: ExcpPartner[]) => {
    setRowData((prev) => {
      const array = [...prev, ...(items || [])];
      const ids = array.map((o) => o.excpId).filter((o) => !!o);
      return array.filter((o, idx) => ids.indexOf(o.excpId || '') === idx);
    });
  };

  const handleRemoveRow = () => {
    setRowData((prev) => (prev || []).filter((o) => !(checkedItems || []).includes(o)));
    setCheckedItems([]);
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      minWidth: 40,
      width: 40,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'excpId',
      header: String(t('com.label.협력업체 ID', '협력업체 ID')),
      width: colWidthMap.excpId,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      isReadOnly: true,
      width: colWidthMap.excpNm,
      align: 'left',
    },
    {
      binding: 'rpsnNm',
      header: String(t('com.label.대표자명', '대표자명')),
      isReadOnly: true,
      width: colWidthMap.rpsnNm,
      align: 'center',
    },
  ];

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3></h3>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="delRow"
            onClick={handleRemoveRow}
            disableRipple
          >
            {t('com.label.삭제', '삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={380}
        isReadOnly={true}
        isFilter={false}
        autoCheck={true}
        rowSelection={'multiple'}
        allowPinning={false}
        allowSorting={false}
        align="center"
        initialized={onInitialized}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
      />
    </>
  );
});

SearchGrid.displayName = 'SearchGrid';
SelectedGrid.displayName = 'SelectedGrid';
export default PartnerModal;
